import React, {InputHTMLAttributes, useLayoutEffect, useRef} from "react";
import {Phone} from "../../../../api-client";
import './InputNumber.scoped.scss';
import Information from "../../utils/Information";
import {cn} from "../../../../utils";
import formatPhone from "../../../../formatters/formatPhone";

interface Props extends InputHTMLAttributes<HTMLInputElement> {
    phone: Phone;
    onPhoneInput?: (phone: Phone) => void;
    title?: string;
    helpingText?: string;
    valid?: boolean;
    isWhite?: boolean;
}

const NUMBER_DIGITS = 10;

const extractDigits = (raw: string): string => {
  let digits = raw.replace(/\D/g, "");
  if (digits.length === NUMBER_DIGITS + 1 && (digits[0] === "7" || digits[0] === "8")) {
    digits = digits.slice(1);
  }
  return digits.slice(0, NUMBER_DIGITS);
};

type FormattedNumber = {
  text: string;
  digitPositions: number[];
};

const formatLocalNumber = (digits: string): FormattedNumber => {
  let text = "";
  const digitPositions: number[] = [];
  const push = (char: string, digitIndex: number) => {
    text += char;
    digitPositions.push(digitIndex);
  };

  if (digits.length === 0) {
    return {text, digitPositions};
  }

  push("(", -1);
  for (let i = 0; i < Math.min(digits.length, 3); i++) push(digits[i], i);
  if (digits.length >= 3) push(")", -1);
  if (digits.length > 3) {
    push(" ", -1);
    for (let i = 3; i < Math.min(digits.length, 6); i++) push(digits[i], i);
  }
  if (digits.length > 6) {
    push("-", -1);
    for (let i = 6; i < Math.min(digits.length, 8); i++) push(digits[i], i);
  }
  if (digits.length > 8) {
    push("-", -1);
    for (let i = 8; i < Math.min(digits.length, NUMBER_DIGITS); i++) push(digits[i], i);
  }

  return {text, digitPositions};
};

const InputNumber = ({phone, onPhoneInput, title = "Телефон", helpingText, valid, isWhite, ...props}: Props) => {
  const numberElem = useRef<HTMLInputElement>(null);
  const nextCaret = useRef<number | null>(null);

  const digits = phone.number;
  const {text: formattedNumber, digitPositions} = formatLocalNumber(digits);

  useLayoutEffect(() => {
    if (nextCaret.current !== null && numberElem.current) {
      numberElem.current.setSelectionRange(nextCaret.current, nextCaret.current);
      nextCaret.current = null;
    }
  }, [formattedNumber]);

  const setDigits = (newDigits: string, caret: number) => {
    nextCaret.current = caret;
    onPhoneInput && onPhoneInput(new Phone(phone.countryCode, newDigits));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDigits = extractDigits(event.target.value);
    setDigits(newDigits, formatLocalNumber(newDigits).text.length);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const newDigits = extractDigits(event.clipboardData.getData("text"));
    setDigits(newDigits, formatLocalNumber(newDigits).text.length);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Backspace" && event.key !== "Delete") return;
    const input = numberElem.current;
    if (!input) return;
    const {selectionStart, selectionEnd} = input;
    if (selectionStart === null || selectionEnd === null || selectionStart !== selectionEnd) return;

    let targetDigitIndex = -1;
    if (event.key === "Backspace") {
      for (let i = selectionStart - 1; i >= 0; i--) {
        if (digitPositions[i] !== -1) {
          targetDigitIndex = digitPositions[i];
          break;
        }
      }
    } else {
      for (let i = selectionStart; i < digitPositions.length; i++) {
        if (digitPositions[i] !== -1) {
          targetDigitIndex = digitPositions[i];
          break;
        }
      }
    }
    if (targetDigitIndex === -1) return;

    event.preventDefault();
    const newDigits = digits.slice(0, targetDigitIndex) + digits.slice(targetDigitIndex + 1);
    const newCaret = formatLocalNumber(digits.slice(0, targetDigitIndex)).text.length;
    setDigits(newDigits, newCaret);
  };

  return (
    <div
      className={cn(
        "form__input-container",
        `${valid ? "form__input-container--valid" : isWhite? "input-white__container" : "input-number__container"}`
      )}
    >
      <div className="form__input--left">
        <label className="d-block w-100" htmlFor={props.id}>
          <div className={isWhite? "input-white__text": "input-number__text"}>
            {title}
          </div>
          <div className={cn("d-flex g-1 align-items-center")}>
            {!valid && <div className={cn(
              "input-number__code", `${phone.number.length > 0 ? 'form__input--black' : ''}`
            )}>
              {phone.countryCode}
            </div>}
            <input
              value={valid ? formatPhone(phone) : formattedNumber}
              className={cn(
                "form__input",
                `${valid ? "input-number--valid" : isWhite? "input-white__input": "input-number__input"}`,
                `${phone.number.length > 0 ? 'form__input--black' : ''}`
              )}
              onChange={handleChange}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              ref={numberElem}
              disabled={valid}
              inputMode="tel"
              {...props}
            />
          </div>
        </label>
      </div>
      <div className="form__input--right">
        {helpingText && <Information>{helpingText}</Information>}
      </div>
    </div>
  );
};

export default InputNumber;